import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Check, ArrowClockwise } from '@phosphor-icons/react';

// Canonical backend objective values, keyed by the miniwizard's own display
// order — Awareness / Feeding / Selling map onto awareness/education/
// monetizing one-to-one (CONTENT-REGEN-004).
const OBJECTIVE_OPTIONS = [
  {
    value: 'awareness',
    labelKey: 'editor.regenerateModal.objectives.awareness.label',
    hintKey: 'editor.regenerateModal.objectives.awareness.hint',
  },
  {
    value: 'education',
    labelKey: 'editor.regenerateModal.objectives.education.label',
    hintKey: 'editor.regenerateModal.objectives.education.hint',
  },
  {
    value: 'monetizing',
    labelKey: 'editor.regenerateModal.objectives.monetizing.label',
    hintKey: 'editor.regenerateModal.objectives.monetizing.hint',
  },
];

const PILLAR_ALIASES = { feeding: 'education', selling: 'monetizing' };

// Mirrors the backend's own resolution fallback (unsupported/empty pillar ->
// awareness) so the preselected card always matches what a missing
// `objective` in the request would resolve to server-side.
function pillarToObjective(pillar) {
  const key = String(pillar || '').trim().toLowerCase();
  if (OBJECTIVE_OPTIONS.some(option => option.value === key)) return key;
  return PILLAR_ALIASES[key] || 'awareness';
}

export default function ContentRegenerationModal({
  open,
  currentPillar,
  regenerating,
  error,
  onCancel,
  onConfirm,
}) {
  const { t } = useTranslation();
  const [selectedObjective, setSelectedObjective] = useState(() => pillarToObjective(currentPillar));

  // Reset the selection from the content's current pillar every time the
  // modal is (re)opened — never carry over a choice from a previous open.
  useEffect(() => {
    if (open) {
      setSelectedObjective(pillarToObjective(currentPillar));
    }
  }, [open, currentPillar]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[220] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.7)' }}
      onClick={() => { if (!regenerating) onCancel(); }}
      data-testid="regen-modal"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="card w-full max-w-lg p-5"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="font-semibold text-lg mb-1">{t('editor.regenerateModal.title')}</h3>
        <p className="text-sm text-[var(--text-secondary)] mb-4">{t('editor.regenerateModal.subtitle')}</p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
          {OBJECTIVE_OPTIONS.map(option => {
            const isSelected = selectedObjective === option.value;
            return (
              <button
                key={option.value}
                type="button"
                data-testid={`regen-objective-${option.value}`}
                className="card text-left p-3"
                style={{
                  border: isSelected ? '2px solid var(--gradient-start)' : '1px solid var(--border-color)',
                  background: isSelected ? 'rgba(99,102,241,0.08)' : undefined,
                }}
                disabled={regenerating}
                onClick={() => setSelectedObjective(option.value)}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-sm">{t(option.labelKey)}</span>
                  {isSelected && <Check weight="bold" size={14} color="var(--gradient-start)" />}
                </div>
                <p className="text-xs text-[var(--text-muted)]">{t(option.hintKey)}</p>
              </button>
            );
          })}
        </div>

        <p className="text-xs text-[var(--text-muted)] mb-1">{t('editor.regenerateModal.explanationPrimary')}</p>
        <p className="text-xs text-[var(--text-muted)] mb-4">{t('editor.regenerateModal.explanationSecondary')}</p>

        {error && (
          <div className="text-sm text-[var(--accent-pink)] mb-4 p-3 rounded-lg" style={{ background: 'rgba(236,72,153,0.1)' }}>
            {error}
          </div>
        )}

        <div className="flex gap-2">
          <button
            className="btn-ghost flex-1"
            disabled={regenerating}
            onClick={onCancel}
            data-testid="regen-cancel-btn"
          >
            {t('common.cancel')}
          </button>
          <button
            className="btn-gradient flex-1"
            disabled={regenerating}
            onClick={() => onConfirm(selectedObjective)}
            data-testid="regen-confirm-btn"
          >
            {regenerating
              ? t('editor.regenerateModal.confirmCtaLoading')
              : <><ArrowClockwise size={15} /> {t('editor.regenerateModal.confirmCta')}</>}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
